import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection, query, where, orderBy, getDocs,
  addDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import {
  getMemberAllRegistrations, getEvent, cancelRegistration,
  getQRCodeUrl, fmtDateTime, daysUntil, EVENT_CATEGORIES,
} from '../../../api/eventsApi';

// ── Constants ─────────────────────────────────────────────────────────────────

const APPOINTMENT_TYPES = [
  'General Consultation',
  'Case Review',
  'Programme Enquiry',
  'Financial Assistance Query',
  'Counselling Session',
  'Follow-up Meeting',
  'Other',
];

const APPT_STATUS = {
  pending:   { label: 'Pending',   color: '#D97706', bg: '#FEF3C7' },
  confirmed: { label: 'Confirmed', color: '#059669', bg: '#D1FAE5' },
  cancelled: { label: 'Cancelled', color: '#DC2626', bg: '#FEE2E2' },
  completed: { label: 'Completed', color: '#6B7280', bg: '#F3F4F6' },
};

const CATEGORY_COLORS = {
  CIRCLE_TIME:         '#0D9488',
  TALK_WORKSHOP:       '#4338CA',
  SOCIAL_RECREATIONAL: '#F97316',
  OTHER:               '#6B7280',
};

const DAYS = [
  { key: 'monday',    label: 'Monday'    },
  { key: 'tuesday',   label: 'Tuesday'   },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday',  label: 'Thursday'  },
  { key: 'friday',    label: 'Friday'    },
  { key: 'saturday',  label: 'Saturday'  },
  { key: 'sunday',    label: 'Sunday'    },
];

const DAY_KEYS = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

// ── SVG Icons ─────────────────────────────────────────────────────────────────

const IC = { display: 'inline', verticalAlign: 'middle' };
const svg = (paths, size, color) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={IC}>
    {paths}
  </svg>
);

const IconCalendar = ({ size = 14, color = 'currentColor' }) => svg(<>
  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
  <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
</>, size, color);

const IconClock = ({ size = 14, color = 'currentColor' }) => svg(<>
  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
</>, size, color);

const IconClipboard = ({ size = 14, color = 'currentColor' }) => svg(<>
  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
  <rect x="8" y="2" width="8" height="4" rx="1"/>
</>, size, color);

const IconMapPin = ({ size = 14, color = 'currentColor' }) => svg(<>
  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
</>, size, color);

const IconPin = ({ size = 14, color = 'currentColor' }) => svg(<>
  <line x1="12" y1="17" x2="12" y2="22"/>
  <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17z"/>
</>, size, color);

// ── Helpers ───────────────────────────────────────────────────────────────────

function catLabel(value) {
  return EVENT_CATEGORIES.find(c => c.value === value)?.label || value;
}

function fmt12(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  return `${hour > 12 ? hour - 12 : hour || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
}

function fmtApptDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-SG', {
    weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
  });
}

// ── My Events section ─────────────────────────────────────────────────────────

function MyEventsSection({ orgId, memberId }) {
  const [registeredEvents, setRegisteredEvents] = useState([]);
  const [regLoading, setRegLoading]             = useState(true);
  const [regError, setRegError]                 = useState('');
  const [cancelTarget, setCancelTarget]         = useState(null);
  const [cancelReason, setCancelReason]         = useState('');
  const [cancelLoading, setCancelLoading]       = useState(false);
  const [qrModal, setQrModal]                   = useState(null);
  const [actionMsg, setActionMsg]               = useState('');

  const showMsg = (msg) => {
    setActionMsg(msg);
    setTimeout(() => setActionMsg(''), 3500);
  };

  const loadRegistered = useCallback(async () => {
    if (!orgId || !memberId) { setRegLoading(false); return; }
    setRegLoading(true); setRegError('');
    try {
      const regs = await getMemberAllRegistrations(orgId, memberId);
      const enriched = await Promise.all(
        regs
          .filter(r => r.status !== 'CANCELLED')
          .map(async reg => {
            const evt = await getEvent(orgId, reg.event_id);
            return evt ? { reg, event: evt } : null;
          })
      );
      setRegisteredEvents(
        enriched
          .filter(Boolean)
          .sort((a, b) => {
            const toMs = ts => ts?.toDate ? ts.toDate().getTime() : new Date(ts || 0).getTime();
            return toMs(a.event.start_datetime) - toMs(b.event.start_datetime);
          })
      );
    } catch {
      setRegError('Failed to load your event registrations.');
    } finally {
      setRegLoading(false);
    }
  }, [orgId, memberId]);

  useEffect(() => { loadRegistered(); }, [loadRegistered]);

  const handleCancelReg = async () => {
    if (!cancelTarget || !cancelReason.trim()) return;
    setCancelLoading(true);
    try {
      await cancelRegistration(orgId, cancelTarget.event.id, cancelTarget.reg.id, cancelReason, memberId);
      showMsg(`Registration for "${cancelTarget.event.title}" cancelled.`);
      setCancelTarget(null); setCancelReason('');
      await loadRegistered();
    } catch {
      showMsg('Cancellation failed. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  if (regLoading) return <SkeletonList />;
  if (regError) return <div style={{ color: '#DC2626', fontSize: 13 }}>{regError}</div>;

  return (
    <div>
      {actionMsg && (
        <div style={{ marginBottom: 14, padding: '8px 14px', background: '#D1FAE5', borderRadius: 6, fontSize: 13, color: '#065F46' }}>
          {actionMsg}
        </div>
      )}

      {registeredEvents.length === 0 ? (
        <div className="bb-member-home-empty">
          <div className="bb-member-home-empty-icon"><IconCalendar size={40} color="#9CA3AF" /></div>
          <p className="bb-member-home-empty-msg">No event registrations yet.</p>
          <p className="bb-member-home-empty-sub">Register for events to see them here.</p>
        </div>
      ) : (
        registeredEvents.map(({ reg, event }) => {
          const catColor = CATEGORY_COLORS[event.category] || '#6B7280';
          const days     = daysUntil(event.start_datetime);
          const qrUrl    = reg.qr_code_token ? getQRCodeUrl(reg.qr_code_token, 200) : null;
          return (
            <div key={reg.id} style={{ borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 16, background: '#fff' }}>
              <div style={{ background: catColor, height: 6 }} />
              <div style={{ padding: '14px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: catColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {catLabel(event.category)}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#D1FAE5', color: '#065F46' }}>
                        Registered
                      </span>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 4 }}>{event.title}</div>
                    <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 2 }}>
                      {fmtDateTime(event.start_datetime)}{event.end_datetime && ` – ${fmtDateTime(event.end_datetime)}`}
                    </div>
                    {event.location?.name && (
                      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>
                        📍 {event.location.name}{event.location.address ? `, ${event.location.address}` : ''}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                    {days !== null && days > 0 && (
                      <span style={{ fontSize: 11, color: '#6B7280' }}>in {days}d</span>
                    )}
                    <button
                      onClick={() => { setCancelTarget({ reg, event }); setCancelReason(''); }}
                      style={{
                        padding: '6px 14px', borderRadius: 6,
                        border: '1.5px solid #DC2626', background: '#fff',
                        color: '#DC2626', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      Cancel Registration
                    </button>
                    {qrUrl && (
                      <button
                        onClick={() => setQrModal({ url: qrUrl, title: event.title })}
                        style={{
                          padding: '6px 14px', borderRadius: 6,
                          border: '1.5px solid #4338CA', background: '#EEF2FF',
                          color: '#4338CA', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        View QR Code
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}

      {/* Cancel registration modal */}
      {cancelTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 420, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, color: '#111827' }}>Cancel Registration</h3>
            <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 16px' }}>
              Cancel your registration for <strong>{cancelTarget.event.title}</strong>? Please give a reason:
            </p>
            <textarea
              rows={3}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation…"
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setCancelTarget(null); setCancelReason(''); }}
                style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #D1D5DB', background: '#fff', fontSize: 13, cursor: 'pointer' }}
              >
                Back
              </button>
              <button
                onClick={handleCancelReg}
                disabled={cancelLoading || !cancelReason.trim()}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: 'none',
                  background: cancelLoading || !cancelReason.trim() ? '#FCA5A5' : '#DC2626',
                  color: '#fff', fontSize: 13, fontWeight: 600,
                  cursor: cancelLoading || !cancelReason.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {cancelLoading ? 'Cancelling…' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code modal */}
      {qrModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 300, width: '90%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#111827' }}>Your QR Code</h3>
            <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 16px' }}>{qrModal.title}</p>
            <img src={qrModal.url} alt="QR Code" style={{ width: 200, height: 200, borderRadius: 8, border: '1px solid #E5E7EB' }} />
            <p style={{ fontSize: 11, color: '#9CA3AF', margin: '12px 0 0' }}>Show this at the event for check-in</p>
            <button
              onClick={() => setQrModal(null)}
              style={{ marginTop: 16, padding: '8px 28px', borderRadius: 6, border: 'none', background: '#4338CA', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function MemberHome({
  orgId, memberId, memberName, memberEmail,
  officeHours, orgName, orgConfig,
  activeTab, scrollSignal,
}) {
  // Section refs for scroll-to
  const appointmentsRef = useRef(null);
  const eventsRef       = useRef(null);
  const officeHoursRef  = useRef(null);

  const refMap = {
    'Appointments': appointmentsRef,
    'Events':       eventsRef,
    'Office Hours': officeHoursRef,
  };

  useEffect(() => {
    if (!scrollSignal) return;
    const target = refMap[activeTab];
    if (target?.current) {
      setTimeout(() => {
        if (target.current) target.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [scrollSignal]); // eslint-disable-line

  // ── Appointments state ──────────────────────────────────────────────────────
  const [appointments, setAppointments] = useState([]);
  const [apptLoading,  setApptLoading]  = useState(true);
  const [showForm,     setShowForm]     = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [apptToast,    setApptToast]    = useState('');
  const [form, setForm] = useState({
    type: APPOINTMENT_TYPES[0],
    preferredDate: '',
    preferredTime: '',
    alternateDate: '',
    notes: '',
  });

  const loadAppointments = useCallback(async () => {
    if (!orgId || !memberId) return;
    setApptLoading(true);
    try {
      const q = query(
        collection(db, 'appointments'),
        where('orgId', '==', orgId),
        where('memberId', '==', memberId),
        orderBy('createdAt', 'desc'),
      );
      const snap = await getDocs(q);
      setAppointments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('MemberHome appointments error:', err);
    } finally {
      setApptLoading(false);
    }
  }, [orgId, memberId]);

  useEffect(() => { loadAppointments(); }, [loadAppointments]);

  const handleApptSubmit = async () => {
    if (!form.preferredDate || !form.preferredTime) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'appointments'), {
        orgId, memberId,
        memberName: memberName || '',
        memberEmail: memberEmail || '',
        type: form.type,
        preferredDate: form.preferredDate,
        preferredTime: form.preferredTime,
        alternateDate: form.alternateDate || '',
        notes: form.notes || '',
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setApptToast("Appointment request submitted! We'll confirm shortly.");
      setShowForm(false);
      setForm({ type: APPOINTMENT_TYPES[0], preferredDate: '', preferredTime: '', alternateDate: '', notes: '' });
      await loadAppointments();
    } catch (err) {
      console.error('MemberHome submit appt error:', err);
      setApptToast('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
      setTimeout(() => setApptToast(''), 4000);
    }
  };


  // ── Office Hours derived ────────────────────────────────────────────────────
  const schedule  = officeHours?.schedule || null;
  const closures  = (officeHours?.specialClosures || [])
    .filter(c => c.date >= new Date().toISOString().slice(0, 10))
    .sort((a, b) => a.date.localeCompare(b.date));

  const todayDayKey = DAY_KEYS[new Date().getDay()];
  const todayStr    = new Date().toISOString().slice(0, 10);
  const todayClosure = (officeHours?.specialClosures || []).find(c => c.date === todayStr);
  const todaySchedule = schedule?.[todayDayKey];
  const todayStatus = todayClosure
    ? { open: false, reason: todayClosure.name || 'Special Closure' }
    : todaySchedule?.open
      ? { open: true, openTime: todaySchedule.openTime, closeTime: todaySchedule.closeTime }
      : { open: false, reason: 'Closed today' };

  // ── Org branding ────────────────────────────────────────────────────────────
  const galleryUrls = orgConfig?.branding?.images?.gallery || [];
  const logoUrl     = orgConfig?.branding?.logo?.url       || null;
  const displayOrgName = orgConfig?.branding?.name || orgConfig?.aboutUs?.name || orgName || orgId;

  const upcoming = appointments.filter(a => a.status !== 'cancelled' && a.status !== 'completed');
  const past     = appointments.filter(a => a.status === 'completed' || a.status === 'cancelled');

  return (
    <div className="bb-member-home-page">

      {/* ── Logo header ─────────────────────────────────────────────── */}
      {logoUrl ? (
        <div className="bb-member-home-logo-header">
          <img src={logoUrl} alt="Organisation logo" className="bb-member-home-logo-centered" />
        </div>
      ) : (
        <div className="bb-member-home-welcome-bar">
          <div>
            <div className="bb-member-home-welcome-org">{displayOrgName}</div>
            <div className="bb-member-home-welcome-sub">Member Portal</div>
          </div>
        </div>
      )}

      {/* ── Gallery ─────────────────────────────────────────────────── */}
      {galleryUrls.length > 0 && (
        <div className="bb-member-home-gallery">
          {galleryUrls.map((url, i) => (
            <img key={i} src={url} alt={`Gallery ${i + 1}`} className="bb-member-home-gallery-img" />
          ))}
        </div>
      )}

      {/* ── Appointments ──────────────────────────────────────────────── */}
      <section ref={appointmentsRef} id="section-appointments" className="bb-member-home-card">

        <div className="bb-member-home-heading">
          <h2>Appointments</h2>
          <button
            className="bb-member-home-action-btn"
            onClick={() => setShowForm(v => !v)}
          >
            {showForm ? '✕ Cancel' : '+ Request Appointment'}
          </button>
        </div>

        {/* Booking form */}
        {showForm && (
          <div className="bb-member-home-form-card">
            <h3 className="bb-member-home-form-title">New Appointment Request</h3>
            <p className="bb-member-home-form-hint">
              Submit your preferred time and we'll confirm your appointment by the next working day.
            </p>

            <div className="bb-member-appt-form-grid">
              <div className="bb-member-appt-form-group">
                <label>Appointment Type</label>
                <select
                  className="bb-member-appt-select"
                  value={form.type}
                  onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
                >
                  {APPOINTMENT_TYPES.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="bb-member-appt-form-group">
                <label>Preferred Date *</label>
                <input
                  type="date"
                  className="bb-member-appt-input"
                  min={new Date().toISOString().slice(0, 10)}
                  value={form.preferredDate}
                  onChange={e => setForm(p => ({ ...p, preferredDate: e.target.value }))}
                />
              </div>

              <div className="bb-member-appt-form-group">
                <label>Preferred Time *</label>
                <input
                  type="time"
                  className="bb-member-appt-input"
                  value={form.preferredTime}
                  onChange={e => setForm(p => ({ ...p, preferredTime: e.target.value }))}
                />
              </div>

              <div className="bb-member-appt-form-group">
                <label>Alternate Date (optional)</label>
                <input
                  type="date"
                  className="bb-member-appt-input"
                  min={new Date().toISOString().slice(0, 10)}
                  value={form.alternateDate}
                  onChange={e => setForm(p => ({ ...p, alternateDate: e.target.value }))}
                />
              </div>
            </div>

            <div className="bb-member-appt-form-group" style={{ marginTop: 4 }}>
              <label>Additional Notes (optional)</label>
              <textarea
                className="bb-member-appt-textarea"
                rows={3}
                placeholder="Share anything you'd like us to know before the appointment…"
                value={form.notes}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                className="bb-member-btn bb-member-btn-primary"
                onClick={handleApptSubmit}
                disabled={submitting || !form.preferredDate || !form.preferredTime}
              >
                {submitting ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        )}

        {/* Appointments list */}
        {apptLoading ? (
          <SkeletonList />
        ) : upcoming.length === 0 && past.length === 0 ? (
          <div className="bb-member-home-empty">
            <div className="bb-member-home-empty-icon"><IconClipboard size={40} color="#9CA3AF" /></div>
            <p className="bb-member-home-empty-msg">No appointments yet.</p>
            <p className="bb-member-home-empty-sub">
              Click "Request Appointment" above to book a consultation.
            </p>
          </div>
        ) : (
          <>
            {upcoming.length > 0 && (
              <>
                <div className="bb-member-home-sub-label">Upcoming &amp; Pending</div>
                <div className="bb-member-home-appt-list">
                  {upcoming.map(a => <ApptCard key={a.id} appt={a} />)}
                </div>
              </>
            )}
            {past.length > 0 && (
              <>
                <div className="bb-member-home-sub-label" style={{ marginTop: 20 }}>Past Appointments</div>
                <div className="bb-member-home-appt-list">
                  {past.map(a => <ApptCard key={a.id} appt={a} />)}
                </div>
              </>
            )}
          </>
        )}

        {apptToast && (
          <div className={`bb-member-toast${apptToast.includes('Failed') ? ' error' : ''}`}>
            {apptToast}
          </div>
        )}
      </section>

      {/* ── My Events ─────────────────────────────────────────────────── */}
      <section ref={eventsRef} id="section-events" className="bb-member-home-card">
        <div className="bb-member-home-heading">
          <h2>My Events</h2>
        </div>
        <MyEventsSection orgId={orgId} memberId={memberId} />
      </section>

      {/* ── Office Hours ───────────────────────────────────────────────── */}
      <section ref={officeHoursRef} id="section-office-hours" className="bb-member-home-card">

        <div className="bb-member-home-heading">
          <h2>Office Hours</h2>
        </div>

        {!schedule ? (
          <div className="bb-member-home-empty">
            <div className="bb-member-home-empty-icon"><IconClock size={40} color="#9CA3AF" /></div>
            <p className="bb-member-home-empty-msg">Office hours not configured yet.</p>
            <p className="bb-member-home-empty-sub">Please contact {displayOrgName} directly for availability.</p>
          </div>
        ) : (
          <>
            {/* Today's status banner */}
            <div className={`bb-member-home-today-banner${todayStatus.open ? ' open' : ' closed'}`}>
              <span className="bb-member-home-today-dot" />
              <span className="bb-member-home-today-status">
                {todayStatus.open ? 'Open Today' : 'Closed Today'}
              </span>
              <span className="bb-member-home-today-detail">
                {todayStatus.open
                  ? `${fmt12(todayStatus.openTime)} – ${fmt12(todayStatus.closeTime)}`
                  : todayStatus.reason}
              </span>
            </div>

            <div className="bb-member-home-hours-grid">
              {/* Weekly schedule */}
              <div>
                <div className="bb-member-home-sub-label">Weekly Schedule</div>
                <div className="bb-member-home-hours-list">
                  {DAYS.map(({ key, label }) => {
                    const day = schedule[key];
                    const isToday = todayDayKey === key;
                    return (
                      <div key={key} className={`bb-member-home-hours-row${isToday ? ' today' : ''}`}>
                        <span className="bb-member-home-hours-day">
                          {label}
                          {isToday && <span className="bb-member-home-today-pill">Today</span>}
                        </span>
                        <span className={`bb-member-home-hours-val${!day?.open ? ' closed' : ''}`}>
                          {day?.open ? `${fmt12(day.openTime)} – ${fmt12(day.closeTime)}` : 'Closed'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Upcoming closures */}
              <div>
                <div className="bb-member-home-sub-label">Upcoming Closures</div>
                {closures.length === 0 ? (
                  <p className="bb-member-home-no-closures">No upcoming closures scheduled.</p>
                ) : (
                  <div className="bb-member-home-closures">
                    {closures.slice(0, 6).map(c => (
                      <div key={c.id || c.date} className="bb-member-home-closure-row">
                        <div className="bb-member-home-closure-date-block">
                          <span className="bb-member-home-closure-month">
                            {new Date(c.date + 'T00:00:00').toLocaleDateString('en-SG', { month: 'short' })}
                          </span>
                          <span className="bb-member-home-closure-day">
                            {new Date(c.date + 'T00:00:00').getDate()}
                          </span>
                        </div>
                        <div>
                          <div className="bb-member-home-closure-name">{c.name}</div>
                          {c.note && <div className="bb-member-home-closure-note">{c.note}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {officeHours?.notes && (
                  <div className="bb-member-home-oh-note">
                    <span className="bb-member-home-oh-note-icon"><IconPin size={14} color="#9CA3AF" /></span>
                    <p>{officeHours.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </section>

    </div>
  );
}

// ── Appointment card ──────────────────────────────────────────────────────────

function ApptCard({ appt }) {
  const statusCfg = APPT_STATUS[appt.status] || APPT_STATUS.pending;
  return (
    <div className="bb-member-home-appt-card">
      <div className="bb-member-home-appt-card-header">
        <div className="bb-member-home-appt-card-service">
          <span className="bb-member-home-appt-type">{appt.type}</span>
        </div>
        <span
          className="bb-member-home-appt-badge"
          style={{ color: statusCfg.color, background: statusCfg.bg }}
        >
          {statusCfg.label}
        </span>
      </div>

      <div className="bb-member-home-appt-card-body">
        <div className="bb-member-home-appt-detail">
          <IconCalendar /> {fmtApptDate(appt.preferredDate)}
          {appt.preferredTime && <span style={{ marginLeft: 12 }}><IconClock /> {appt.preferredTime}</span>}
        </div>
        {appt.notes && (
          <div className="bb-member-home-appt-notes">"{appt.notes}"</div>
        )}
      </div>
    </div>
  );
}

// ── Skeleton loader ───────────────────────────────────────────────────────────

function SkeletonList() {
  return (
    <div className="skel-appt-list">
      {[1, 2].map(i => (
        <div key={i} className="skel-appt-card">
          <div className="skel-avatar" />
          <div className="skel-lines">
            <div className="skel-line skel-line--lg" />
            <div className="skel-line skel-line--md" />
            <div className="skel-line skel-line--sm" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default MemberHome;
